import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Image,
  List,
  SimpleGrid,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextInput } from '@/components';
import { useUserStore } from '@/stores';
import registerConfirmationImg from '@/assets/register_confirmed.svg';
import { FiX } from 'react-icons/fi';
import {
  ErrorsList,
  FilesList,
} from '@/features/historic/components/Drawer/Exams/FilesList';
import { api } from '@/lib';
import { IoDownloadOutline } from 'react-icons/io5';

type ExcelFormFields = {
  file: string;
};

interface ExcelFormProps {
  onCancel: () => void;
}

export const excelSchema = yup.object().shape({
  file: yup.string(),
});

export function RegisterUserUpload({ onCancel }: ExcelFormProps) {
  const [formStep, setFormStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [errorsList, setErrors] = useState<any[]>([]);

  const [update, setUpdade] = useState<boolean>(true);
  const changeUpdate = () => {
    setUpdade(!update);
  };

  const [path, setPath] = useState<string>('');

  function handleStepBack() {
    if (formStep === 0) {
      onCancel();
    } else {
      setFormStep(currStep => currStep - 1);
    }
  }

  const {
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    setError,
  } = useForm<ExcelFormFields>({
    defaultValues: {
      file: '',
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: false,
    resolver: yupResolver(excelSchema),
  });

  const toast = useToast({
    variant: 'solid',
    position: 'top-right',
    isClosable: true,
  });

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/exemplo_criar_usuarios.xlsx';
    link.download = 'exemplo_criar_usuarios.xlsx';
    link.click();
  };

  async function onSubmit() {
    setErrors([]);

    if (files.length < 1) {
      setError('file', {
        type: 'manual',
        message: 'Arquivo obrigatório',
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', files[0].file);

    setLoading(true);
    await api
      .post('/usuario/Excel', formData, {
        headers: {
          accept: '*/*',
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(async response => {
        setFormStep(currStep => currStep + 1);
      })
      .catch(error => {
        if (error.response && error.response.data) {
          const { errors: err } = error.response.data;
          setErrors(err);
        }
        toast({
          title: 'Erro ao cadastrar usuário',
          status: 'error',
          duration: 9000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      {formStep === 0 ? (
        <VStack spacing="4">
          <TextInput
            pt="2"
            label="Arquivo"
            type="file"
            accept=".xlsx, .xls, .csv"
            iconView={FiX}
            onChange={c => {
              setErrors([]);
              if (c.target.files) {
                const { files: fileInput } = c.target;
                const reader = new FileReader();
                reader.onloadend = () => {
                  setFiles([
                    {
                      file: fileInput[0],
                      id: files.length,
                      // @ts-ignore
                      base64: reader.result.split(',')[1],
                      type: fileInput[0].type.split('/')[1],
                    },
                  ]);
                  changeUpdate();
                };
                reader.readAsDataURL(fileInput[0]);
              }
            }}
            error={errors.file}
          />
          {files && files.length > 0 && (
            <List
              mb="10"
              h="100%"
              backgroundColor="#FFF"
              style={{
                width: '100%',
              }}
              sx={{
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { borderRadius: '8px' },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '15',
                  bg: '#bfbfbf',
                },
              }}
            >
              <FilesList
                files={files}
                onDelete={(id: any) => {
                  setFiles(prevState =>
                    prevState.filter(file => file.id !== id),
                  );
                  setValue('file', '');
                  setErrors([]);
                  changeUpdate();
                }}
              />
            </List>
          )}

          {errorsList && errorsList.length > 0 && (
            <>
              <Heading
                mt="6"
                mb="4"
                color="red"
                fontSize="xl"
                fontWeight="medium"
              >
                Erros encontrados
              </Heading>
              <List
                mb="10"
                h="100%"
                backgroundColor="#FFF"
                style={{
                  width: '100%',
                }}
                sx={{
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { borderRadius: '8px' },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '15',
                    bg: '#bfbfbf',
                  },
                }}
              >
                <ErrorsList errors={errorsList} />
              </List>
            </>
          )}

          <SimpleGrid minChildWidth="180px" spacing="4" w="100%">
            <Button
              type="button"
              isDisabled={loading}
              isLoading={loading}
              onClick={downloadTemplate}
              variant="outline"
              colorScheme="brand-orange"
              h="45px"
              borderRadius="28px"
              color="brand-orange.300"
              fontSize="xs"
              fontWeight="medium"
              textTransform="uppercase"
            >
              <Icon
                as={IoDownloadOutline}
                fontSize="20px"
                marginRight="5"
                color="brand-orange.300"
              />
              Arquivo Modelo
            </Button>

            <Button
              isDisabled={loading}
              isLoading={loading}
              type="submit"
              colorScheme="brand-green"
              h="45px"
              borderRadius="28px"
              color="white"
              fontSize="xs"
              fontWeight="medium"
              textTransform="uppercase"
            >
              Continuar
            </Button>
          </SimpleGrid>
        </VStack>
      ) : (
        <Flex w="100%" direction="column" align="center">
          <Heading
            color="brand-dark.300"
            fontSize="md"
            fontWeight="medium"
            textAlign="center"
          >
            Cadastro Realizado com Sucesso!
          </Heading>

          <Image
            mt="8"
            mb="4"
            height="40"
            src={registerConfirmationImg}
            alt="Mulher confirmando o sucesso de uma operação em um celular grande"
          />

          <Text
            color="brand-dark.200"
            fontSize="sm"
            fontWeight="normal"
            textAlign="center"
          >
            Você pode editar as configurações na aba de cadastros.
          </Text>

          <Link
            to="app/patientsList"
            style={{ color: 'inherit', textDecoration: 'none' }}
            replace
          >
            <Button
              type="submit"
              onClick={onCancel}
              colorScheme="brand-green"
              mt="10"
              h="45px"
              borderRadius="28px"
              color="white"
              fontSize="xs"
              fontWeight="medium"
              textTransform="uppercase"
            >
              Ir para cadastros
            </Button>
          </Link>
        </Flex>
      )}
    </Box>
  );
}
